import { useState, type SubmitEventHandler } from 'react';

import { isPlausibleScore } from '@/core/engine/matchRules';
import type { Match, MatchRuleSet, Team, TeamId } from '@/core/schemas';
import { sideLabel } from '@/features/matches/matchPresentation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';

interface DirectorResultFormProps {
  match: Match;
  rules: MatchRuleSet | null;
  lookup: (teamId: TeamId) => Team | undefined;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (result: { winnerTeamId: TeamId | null; legs: { home: number; away: number } }) => void;
}

/**
 * The director's result editor. Unlike the captain form it may overrule an
 * existing result, so it prefills the current score and is used for both
 * conflict resolution and corrections.
 */
export function DirectorResultForm({
  match,
  rules,
  lookup,
  isSubmitting,
  errorMessage,
  onSubmit,
}: DirectorResultFormProps) {
  const homeTeamId = match.home.kind === 'team' ? match.home.teamId : null;
  const awayTeamId = match.away.kind === 'team' ? match.away.teamId : null;

  const [home, setHome] = useState(String(match.result?.score.legs.home ?? ''));
  const [away, setAway] = useState(String(match.result?.score.legs.away ?? ''));
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setLocalError(null);

    const h = Number(home);
    const a = Number(away);

    if (
      home.trim() === '' ||
      away.trim() === '' ||
      !Number.isInteger(h) ||
      !Number.isInteger(a) ||
      h < 0 ||
      a < 0
    ) {
      setLocalError('Bitte für beide Seiten ganze, nicht negative Zahlen eintragen.');
      return;
    }

    if (h === a) {
      setLocalError('Bitte einen Sieger festlegen — ein Unentschieden ist nicht möglich.');
      return;
    }

    if (rules !== null && !isPlausibleScore({ home: h, away: a }, rules)) {
      setLocalError(`Dieses Ergebnis passt nicht zum Modus "Best of ${rules.bestOf}".`);
      return;
    }

    const winnerTeamId = h > a ? homeTeamId : awayTeamId;
    onSubmit({ winnerTeamId, legs: { home: h, away: a } });
  };

  if (homeTeamId === null || awayTeamId === null) {
    return <Alert tone="info">Die Teilnehmer stehen noch nicht fest.</Alert>;
  }

  const message = errorMessage ?? localError;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      {message !== null && <Alert tone="danger">{message}</Alert>}

      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="truncate font-medium">{sideLabel(match.home, lookup)}</span>
          <input
            type="number"
            min={0}
            value={home}
            onChange={(event) => setHome(event.target.value)}
            className="bg-surface-raised border-line touch-target rounded-lg border px-3 py-3 text-center text-lg font-semibold"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="truncate font-medium">{sideLabel(match.away, lookup)}</span>
          <input
            type="number"
            min={0}
            value={away}
            onChange={(event) => setAway(event.target.value)}
            className="bg-surface-raised border-line touch-target rounded-lg border px-3 py-3 text-center text-lg font-semibold"
          />
        </label>
      </div>

      <Button type="submit" isLoading={isSubmitting} className="sm:self-start">
        Ergebnis festlegen
      </Button>
    </form>
  );
}
